import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, signal, WritableSignal } from "@angular/core";
import { BillingService } from "../billing.service";
import { LoggerService } from "@teammood/shared/util/logger";
import { mergeMap, Subject, takeUntil, tap } from "rxjs";
import { Sort } from "@angular/material/sort";
import { LicenceUsageWithDiff, TeamLicenceDetails } from "../billing.type";
import { FormControl } from "@angular/forms";
import { format } from "date-fns-tz";

@Component({
    selector: "yearly-billing-details-by-team",
    templateUrl: "./yearly-billing-details-by-team.component.html",
    providers: [
        BillingService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class YearlyBillingDetailsByTeamComponent implements OnInit, OnDestroy {

    protected teamsDetails: WritableSignal<TeamLicenceDetails[]> = signal([]);

    protected loading: WritableSignal<boolean> = signal(true);
    protected totalSelectedMonth = signal(0)
    protected totalCurrentMonth: WritableSignal<LicenceUsageWithDiff> = signal({count: 0, diff: 0});

    protected months: MonthWithLabel[] = [];
    protected monthsCtrl: FormControl<MonthWithLabel> = new FormControl<MonthWithLabel>(this.months[0]);

    private _destroy$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private billingService: BillingService,
    ) {}

    ngOnInit(): void {
        this.billingService.get_yearly_subscription_details_by_team().pipe(
            tap(details => {
                this.teamsDetails.set(details.teams);
                this.totalCurrentMonth.set(details.totalCurrentMonth);
                this.totalSelectedMonth.set(details.totalSelectedMonth);
                this.months = details.months.map(date => {
                    return {
                        date: date,
                        label: format(date, 'MMM yyyy')
                    }
                });
                this.monthsCtrl.setValue(this.months[0], {emitEvent: false});
                this.loading.set(false);
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.monthsCtrl.valueChanges.pipe(
            mergeMap(month => {
                this.loading.set(true);
                return this.billingService.get_yearly_subscription_details_by_team(month.date);
            }),
            tap(details => {
                this.teamsDetails.set(details.teams);
                this.totalCurrentMonth.set(details.totalCurrentMonth);
                this.totalSelectedMonth.set(details.totalSelectedMonth);
                this.loading.set(false);
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    protected sortData(sort: Sort) {
        const data = this.teamsDetails();
        if (!sort.active || sort.direction === '') {
            this.teamsDetails.set(data);
            return;
        }

        this.teamsDetails.set(
            data.sort((a, b) => {
                const isAsc = sort.direction === 'asc';
                switch (sort.active) {
                    case 'name':
                        return this.compare(a.name, b.name, isAsc);
                    case 'creationDate':
                        return this.compare(a.creationDate, b.creationDate, isAsc);
                    case 'creator':
                        return this.compare(a.creator, b.creator, isAsc);
                    default:
                        return 0;
                }
            }),
        );
    }

    private compare(a: number | string | Date, b: number | string | Date, isAsc: boolean) {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
}

interface MonthWithLabel {
    date: Date,
    label: string
}

@Component({
    selector: "licence-usage-with-diff",
    templateUrl: "./licence-usage-with-diff.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: true,
})
export class LicenceUsageWithDiffComponent {

    @Input()
    licenceUsageWithDiff: LicenceUsageWithDiff;
}